import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { STATUS_COLORS } from "@/theme/theme";
import { useApiMutation, useApiQuery, type WithDynamicUrl } from "../hooks";
import type { Graph, GraphEdge, GraphNode, Test, TestContext, TestContextVersion } from "../types";
import GraphVisualization from "./graph-visualization";
import { TestContextList } from "./test-context-list";

type Props = {
  testId: string;
};

export const TestVersions = ({ testId }: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: versions,
    isLoading,
    refetch: refetchVersions,
  } = useApiQuery<TestContextVersion[]>(`/tests/${testId}/versions`, {
    enabled: !!testId,
  });

  const {
    data: test,
    isLoading: isTestLoading,
    refetch: refetchTest,
  } = useApiQuery<Test>(`/tests/${testId}`, {
    enabled: !!testId,
  });

  const { mutate: applyVersion, isPending: isApplyingVersion } = useApiMutation<
    WithDynamicUrl<void>,
    void
  >("POST", "", {
    onSuccess: () => {
      enqueueSnackbar("Version applied successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      refetchVersions();
      refetchTest();
    },
  });

  const [tab, setTab] = useState("versions");

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const isCurrentVersion = useCallback(
    (hash: string) => {
      return hash === test?.version_hash;
    },
    [test],
  );

  const versionGraph: Graph = useMemo(() => {
    if (!versions) return { nodes: [], edges: [] };

    const nodes = versions.map(
      (v) =>
        ({
          id: v.hash,
          label: v.hash.substring(0, 8),
          type: isCurrentVersion(v.hash) ? "pinnedVersion" : "version",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any as GraphNode,
    );

    const edges = versions
      .map((v) => {
        return v.prev_hash
          ? {
              source: v.prev_hash,
              target: v.hash,
            }
          : null;
      })
      .filter(Boolean) as GraphEdge[];

    return { nodes, edges };
  }, [versions, isCurrentVersion]);

  if (isLoading || isTestLoading) {
    return (
      <Stack direction="row" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="body1" ml={2} color="textSecondary">
          Loading versions...
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleChange} aria-label="Test details tabs">
            <Tab label="Versions" value="versions" />
            <Tab label="Graph" value="graph" />
          </TabList>
        </Box>
        <TabPanel value="versions">
          <Stack spacing={2}>
            <Typography variant="body2" gutterBottom>
              The test {test?.name} has {versions?.length ?? 0} versions.
            </Typography>
            {versions?.map((version) => (
              <Accordion
                key={version.hash}
                sx={{
                  backgroundColor: isCurrentVersion(version.hash)
                    ? STATUS_COLORS.info.bg
                    : "background.paper",
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Tooltip title={version.hash}>
                      <Typography variant="subtitle1">
                        <code>{version.hash.substring(0, 8)}</code>
                        {isCurrentVersion(version.hash) ? " 📌" : ""}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="textSecondary" ml={2}>
                      {format(new Date(version.timestamp), "yyyy-MM-dd HH:mm:ss")}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Divider />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Stack direction="column" spacing={0}>
                        <Typography variant="caption" color="textSecondary">
                          Message
                        </Typography>
                        <Typography variant="body2">
                          {version.message ?? "No message provided"}
                        </Typography>
                      </Stack>
                      <Tooltip title={version.prev_hash}>
                        <Typography variant="caption" color="textSecondary">
                          <code>{version.prev_hash?.substring(0, 8) ?? ""}</code>
                        </Typography>
                      </Tooltip>
                    </Stack>
                    <TestContextList
                      contexts={version.data.map(
                        (ctx) =>
                          ({
                            id: ctx.id.toString(),
                            name: ctx.name,
                            description: ctx.description,
                            end_goal: ctx.end_goal,
                            data: ctx,
                          }) as TestContext,
                      )}
                    />
                    {!isCurrentVersion(version.hash) && (
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          loading={isApplyingVersion}
                          disabled={isApplyingVersion}
                          onClick={() =>
                            applyVersion({
                              _url: `/tests/${testId}/versions/${version.hash}/apply`,
                            })
                          }
                        >
                          Use this version
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </TabPanel>
        <TabPanel value="graph">
          {versionGraph ? (
            <GraphVisualization nodes={versionGraph.nodes} edges={versionGraph.edges} />
          ) : (
            <Typography>No graph data available for this test version.</Typography>
          )}
        </TabPanel>
      </TabContext>
    </Box>
  );
};
